import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
    TablePagination, TextField, IconButton, Grid, Typography, CircularProgress,
    Snackbar, Alert, Stack, MenuItem, Select, FormControl, InputLabel, Button,
    Chip, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
    Divider, InputAdornment
} from "@mui/material";
import {
    Visibility as VisibilityIcon,
    CheckCircle as CheckCircleIcon,
    Payment as PaymentIcon,
    Lock as LockIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Payments as PaymentsIcon,
    AccountBalanceWallet as AccountBalanceWalletIcon,
    Close as CloseIcon,
    PendingActions as PendingIcon,
    RequestQuote as RequestQuoteIcon
} from "@mui/icons-material";
import { getData } from "../../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../../services/endpoints";
import StatsCard from "../../../../components/common/StatsCard";

/**
 * PayrollBatch Component
 * Renders overview stats, batch records, search/filtering, employee payroll details breakdown,
 * edit LOP, lock/finalize batch, and disburse payouts.
 */
function PayrollBatch({ payPeriod }) {
    const [batchList, setBatchList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // Search & Filter States
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Detail Breakdown Dialog
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Feedback Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const showMessage = (message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    // Helper to parse payPeriod string e.g. "September 2026" or "2026-09"
    const parsedPeriod = useMemo(() => {
        if (!payPeriod) return { year: "", shortMonth: "" };
        const parts = payPeriod.trim().split(" ");
        if (parts.length >= 2) {
            const monthName = parts[0];
            const year = parts[1];
            const d = new Date(`${monthName} 1, 2020`);
            const shortMonth = isNaN(d) ? monthName : d.toLocaleString("en-US", { month: "short" });
            return { year, shortMonth };
        } else if (payPeriod.includes("-")) {
            const [y, m] = payPeriod.split("-");
            const d = new Date(Number(y), Number(m) - 1, 1);
            return {
                year: y,
                shortMonth: d.toLocaleString("en-US", { month: "short" })
            };
        }
        return { year: "", shortMonth: "" };
    }, [payPeriod]);

    // Fetch Batch Records
    const fetchBatchData = useCallback(async () => {
        if (!payPeriod || !parsedPeriod.year || !parsedPeriod.shortMonth) {
            setBatchList([]);
            return;
        }

        setLoading(true);
        try {
            const res = await getData(API_ENDPOINTS.getBatchOverview(parsedPeriod.year, parsedPeriod.shortMonth));
            if (res?.statusCode === 200 && Array.isArray(res.result)) {
                setBatchList(res.result);
            } else {
                setBatchList([]);
            }
        } catch (error) {
            setBatchList([]);
            showMessage("Failed to load batch payroll data.", "error");
        } finally {
            setLoading(false);
        }
    }, [payPeriod, parsedPeriod]);

    useEffect(() => {
        fetchBatchData();
    }, [fetchBatchData]);

    // Summary Metrics
    const summaryMetrics = useMemo(() => {
        const totalEmployees = batchList.length;
        let totalGross = 0;
        let totalDeductions = 0;
        let totalReimbursements = 0;
        let netPayable = 0;
        let lockedCount = 0;
        let finalizedCount = 0;
        let paidCount = 0;

        batchList.forEach((emp) => {
            const gross = parseFloat(emp.totalGross || emp.grossSalary || 0);
            const deductions = parseFloat(emp.totalDeductions || 0);
            const reimbursements = parseFloat(emp.reimbursementAmount || 0);
            const net = parseFloat(emp.netSalary || (gross - deductions + reimbursements));

            totalGross += gross;
            totalDeductions += deductions;
            totalReimbursements += reimbursements;
            netPayable += net;

            if (emp.locked) lockedCount += 1;
            if (emp.finalized) finalizedCount += 1;
            if (emp.payed_flag) paidCount += 1;
        });

        const isFullyLocked = totalEmployees > 0 && lockedCount === totalEmployees;
        const isFullyFinalized = totalEmployees > 0 && finalizedCount === totalEmployees;
        const isFullyPaid = totalEmployees > 0 && paidCount === totalEmployees;

        return {
            totalEmployees,
            totalGross,
            totalDeductions,
            totalReimbursements,
            netPayable,
            lockedCount,
            finalizedCount,
            paidCount,
            isFullyLocked,
            isFullyFinalized,
            isFullyPaid,
        };
    }, [batchList]);

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 2,
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    // Filter logic
    const filteredRecords = useMemo(() => {
        let list = [...batchList];

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            list = list.filter((emp) => {
                const empId = String(emp.empId || emp.employeeId || emp.id || "").toLowerCase();
                const name = String(emp.name || emp.employeeName || "").toLowerCase();
                const dept = String(emp.department || emp.departmentName || "").toLowerCase();
                return empId.includes(query) || name.includes(query) || dept.includes(query);
            });
        }

        if (statusFilter !== "All") {
            list = list.filter((emp) => {
                if (statusFilter === "Paid") return emp.payed_flag === true;
                if (statusFilter === "Finalized") return emp.finalized === true && !emp.payed_flag;
                if (statusFilter === "Locked") return emp.locked === true && !emp.finalized;
                if (statusFilter === "Pending") return !emp.locked && !emp.finalized && !emp.payed_flag;
                return true;
            });
        }

        return list;
    }, [batchList, searchQuery, statusFilter]);

    // Handlers
    const handleOpenDetail = async (row) => {
        setDetailOpen(true);
        setDetailLoading(true);
        setSelectedEmployee(row);
        try {
            if (row.id) {
                const res = await getData(API_ENDPOINTS.getBatchPayrollById(row.id));
                if (res?.statusCode === 200 && res.result) {
                    setSelectedEmployee(res.result);
                }
            }
        } catch (error) {
            // Keep existing row object if API fails
        } finally {
            setDetailLoading(false);
        }
    };

    const handleReset = () => {
        setStatusFilter("All");
        setSearchQuery("");
    };

    // Helper for Status Chip
    const renderStatusChip = (row) => {
        if (row.payed_flag) {
            return <Chip icon={<CheckCircleIcon sx={{ fontSize: 16 }} />} label="Paid" color="success" size="small" variant="filled" />;
        }
        if (row.finalized) {
            return <Chip icon={<PaymentIcon sx={{ fontSize: 16 }} />} label="Finalized" color="primary" size="small" variant="filled" />;
        }
        if (row.locked) {
            return <Chip icon={<LockIcon sx={{ fontSize: 16 }} />} label="Locked" color="warning" size="small" variant="filled" />;
        }
        return <Chip icon={<PendingIcon sx={{ fontSize: 16 }} />} label="Draft" color="default" size="small" variant="outlined" />;
    };

    return (
        <Box sx={{ pb: 3 }}>
            {/* Top Bar Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                    Payroll Batch Details ({payPeriod})
                </Typography>
                <Button
                    startIcon={<RefreshIcon />}
                    variant="outlined"
                    size="small"
                    onClick={fetchBatchData}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            {/* Summary Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item size={{ xs: 12, sm: 6, lg: 3 }}>
                    <StatsCard
                        title="Total Gross Pay"
                        value={formatCurrency(summaryMetrics.totalGross)}
                        icon={<AccountBalanceWalletIcon />}
                        color="#4caf50"
                        sx={{containerType: 'inline-size'}}
                        contentSx={{'@container (max-width: 300px)': {
                            flexDirection: 'column-reverse',
                            alignItems: 'flex-start'
                        }}}
                        displaySx={{fontSize: 15}}
                        valueSx={{fontSize: 20}}
                    />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, lg: 3 }}>
                    <StatsCard
                        title="Total Deductions"
                        value={formatCurrency(summaryMetrics.totalDeductions)}
                        icon={<PaymentsIcon />}
                        color="#f44336"
                        sx={{containerType: 'inline-size'}}
                        contentSx={{'@container (max-width: 300px)': {
                            flexDirection: 'column-reverse',
                            alignItems: 'flex-start'
                        }}}
                        displaySx={{fontSize: 15}}
                        valueSx={{fontSize: 20}}
                    />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, lg: 3 }}>
                    <StatsCard
                        title="Total Reimbursements"
                        value={formatCurrency(summaryMetrics.totalReimbursements)}
                        icon={<RequestQuoteIcon />}
                        color="#65bff7"
                        sx={{containerType: 'inline-size'}}
                        contentSx={{'@container (max-width: 300px)': {
                            flexDirection: 'column-reverse',
                            alignItems: 'flex-start'
                        }}}
                        displaySx={{fontSize: 15}}
                        valueSx={{fontSize: 20}}
                    />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, lg: 3 }}>
                    <StatsCard
                        title="Net Payable"
                        value={formatCurrency(summaryMetrics.netPayable)}
                        icon={<PaymentIcon />}
                        color="#f5b351"
                        sx={{containerType: 'inline-size'}}
                        contentSx={{'@container (max-width: 300px)': {
                            flexDirection: 'column-reverse',
                            alignItems: 'flex-start'
                        }}}
                        displaySx={{fontSize: 15}}
                        valueSx={{fontSize: 20}}
                    />
                </Grid>
            </Grid>

            {/* Controls Bar: Search, Filters, and Actions */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0", borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid size={{xs:12, sm:6, md:3}}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="status-filter-label">Filter Status</InputLabel>
                            <Select
                                labelId="status-filter-label"
                                label="Filter Status"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <MenuItem value="All">All</MenuItem>
                                <MenuItem value="Pending">Pending</MenuItem>
                                <MenuItem value="Locked">Locked</MenuItem>
                                <MenuItem value="Finalized">Finalized</MenuItem>
                                <MenuItem value="Paid">Paid</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{xs:12, sm:6, md:3}}>
                        <Button fullWidth size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={handleReset}>
                            Reset
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Batch Table Container */}
            <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 5 }}>
                        <CircularProgress size={36} />
                    </Box>
                ) : filteredRecords.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: "center" }}>
                        <Typography variant="body1" color="textSecondary">
                            No payroll batch records found for this period.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Table size="medium">
                            <TableHead sx={{ bgcolor: "#f5f7fa" }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: "bold" }}>Employee</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }} align="right">Gross Pay</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }} align="right">Deductions</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }} align="right">Reimbursements</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }} align="right">Net Salary</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }} align="center">LOP (Days)</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }} align="center">Status</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }} align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredRecords
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((row, index) => {
                                        const gross = parseFloat(row.totalGross || row.grossSalary || 0);
                                        const deductions = parseFloat(row.totalDeductions || 0);
                                        const reimbursements = parseFloat(row.reimbursementAmount || 0);
                                        const net = parseFloat(row.netSalary || (gross - deductions + reimbursements));

                                        return (
                                            <TableRow key={row.id || index} hover>
                                                <TableCell>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="600">
                                                                {row.name || row.employeeName || "N/A"}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {row.empId || row.employeeId || row.id || ""}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right">{formatCurrency(gross)}</TableCell>
                                                <TableCell align="right" sx={{ color: "#d32f2f" }}>
                                                    {formatCurrency(deductions)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: "#2e7d32" }}>
                                                    {formatCurrency(reimbursements)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: "bold", color: "#1976d2" }}>
                                                    {formatCurrency(net)}
                                                </TableCell>
                                                <TableCell align="center">{row.lopCount ?? row.lopDays ?? 0}</TableCell>
                                                <TableCell align="center">{renderStatusChip(row)}</TableCell>
                                                <TableCell align="center">
                                                    <Stack direction="row" spacing={0.5} justifyContent="center">
                                                        <Tooltip title="View Detailed Breakdown">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handleOpenDetail(row)}
                                                            >
                                                                <VisibilityIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            component="div"
                            count={filteredRecords.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(e, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                        />
                    </>
                )}
            </TableContainer>

            {/* View Breakdown Modal */}
            <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h6" fontWeight="bold">
                        Payroll Breakdown Details
                    </Typography>
                    <IconButton onClick={() => setDetailOpen(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {detailLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                            <CircularProgress size={30} />
                        </Box>
                    ) : selectedEmployee ? (
                        <Box sx={{ py: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, margin: "0rem 1rem 1.5rem 1rem" }}>
                                <Box>
                                    <Typography variant="h6">{selectedEmployee.name || selectedEmployee.employeeName}</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Emp ID: {selectedEmployee.empId || selectedEmployee.employeeId || selectedEmployee.id}
                                    </Typography>
                                </Box>
                                <Box sx={{ ml: "auto" }}>{renderStatusChip(selectedEmployee)}</Box>
                            </Box>

                            <Grid container spacing={3}>
                                <Grid size={{xs:12, md:6}}>
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                                            Earnings & Gross
                                        </Typography>
                                        <Divider sx={{ mb: 1.5 }} />
                                        <Stack spacing={1}>
                                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                <Typography variant="body2">Basic Pay:</Typography>
                                                <Typography variant="body2" fontWeight="600">{formatCurrency(selectedEmployee.basicPay)}</Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                <Typography variant="body2">HRA / Allowances:</Typography>
                                                <Typography variant="body2" fontWeight="600">{formatCurrency(selectedEmployee.hra)}</Typography>
                                            </Box>
                                            <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                                <Typography variant="body2">Special Allowance</Typography>
                                                <Typography variant="body2" fontWeight="600">{formatCurrency(selectedEmployee.specialAllowance)}</Typography>
                                            </Box>
                                            <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                                <Typography variant="body2">Conveyance Alllowance</Typography>
                                                <Typography variant="body2" fontWeight="600">{formatCurrency(selectedEmployee.conveyanceAllowance)}</Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                <Typography variant="body2">Reimbursements:</Typography>
                                                <Typography variant="body2" fontWeight="600" color="success.main">{formatCurrency(selectedEmployee.reimbursementAmount)}</Typography>
                                            </Box>
                                            <Divider />
                                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                <Typography variant="subtitle2" fontWeight="bold">Total Gross Pay:</Typography>
                                                <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                                                    {formatCurrency(selectedEmployee.totalGross || selectedEmployee.grossSalary)}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                </Grid>

                                <Grid size={{xs:12, md:6}}>
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        <Typography variant="subtitle1" fontWeight="bold" color="error" gutterBottom>
                                            Deductions & Adjustments
                                        </Typography>
                                        <Divider sx={{ mb: 1.5 }} />
                                        <Stack spacing={1}>
                                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                <Typography variant="body2">PF / ESI Deductions:</Typography>
                                                <Typography variant="body2" fontWeight="600">{formatCurrency(selectedEmployee.pfDeduction)}</Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                <Typography variant="body2">Income Tax (TDS):</Typography>
                                                <Typography variant="body2" fontWeight="600">{formatCurrency(selectedEmployee.tdsDeduction)}</Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                <Typography variant="body2">LOP Days ({selectedEmployee.lopCount ?? selectedEmployee.lopDays ?? 0}):</Typography>
                                                <Typography variant="body2" fontWeight="600">{formatCurrency(selectedEmployee.lopDeduction)}</Typography>
                                            </Box>
                                            <Divider />
                                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                <Typography variant="subtitle2" fontWeight="bold">Total Deductions:</Typography>
                                                <Typography variant="subtitle2" fontWeight="bold" color="error.main">
                                                    {formatCurrency(selectedEmployee.totalDeductions)}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                </Grid>

                                {selectedEmployee?.leaveEncashmentAmount > 0 && <Grid size={{xs:12, sm: 6}}>
                                  <Paper variant="outlined" sx={{p:2}}>
                                    <Typography variant="subtitle1" fontWeight="600" color="success">
                                      Others
                                    </Typography>
                                    <Divider sx={{mb: 1.5}}/>
                                    <Stack>
                                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                        <Typography variant="body2">Leave Encashment:</Typography>
                                        <Typography fontWeight="600" variant="body2">{formatCurrency(selectedEmployee.leaveEncashmentAmount)}</Typography>
                                      </Box>
                                    </Stack>
                                  </Paper>
                                </Grid>}

                                <Grid size={{xs:12}}>
                                    <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f0f7ff" }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <Typography variant="h6" fontWeight="bold" color="primary">
                                                Net Payable Amount:
                                            </Typography>
                                            <Typography variant="h5" fontWeight="800" color="primary">
                                                {formatCurrency(selectedEmployee.netSalary)}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Box>
                    ) : (
                        <Typography color="textSecondary">No detailed information available.</Typography>
                    )}
                </DialogContent>
            </Dialog>

            {/* Feedback Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default PayrollBatch;
